import React, { useEffect, useRef, useState } from 'react';
import { IconButton, Slider, Box, Typography, useTheme, Select, MenuItem } from '@mui/material';
import { MdForward10, MdOutlineReplay10, MdOutlineVolumeUp } from 'react-icons/md';
import { IoIosPlay } from 'react-icons/io';
import { TiMediaPauseOutline } from 'react-icons/ti';
import { FiDownload } from 'react-icons/fi';
import { IoTimeOutline } from 'react-icons/io5';

interface MusicPlayerProps {
  url: string;
  isActive?: boolean;
}

const formatTime = (time: number) => {
  if (isNaN(time)) return '0:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60)
    .toString()
    .padStart(2, '0');
  return `${minutes}:${seconds}`;
};

const MusicPlayer: React.FC<MusicPlayerProps> = ({ url, isActive = true }) => {
  const theme = useTheme();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    setIsPlaying(true);
    const playPromise = audio.play();

    if (playPromise !== undefined) {
      playPromise.catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('Audio play failed:', err);
          setIsPlaying(false);
        }
      });
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) setDuration(audioRef.current.duration);
  };

  const handleSeek = (_: Event, newValue: number | number[]) => {
    if (audioRef.current) {
      const seekTime = Array.isArray(newValue) ? newValue[0] : newValue;
      audioRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };

  const handleRewind = () => {
    if (audioRef.current) audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
  };

  const handleForward = () => {
    if (audioRef.current) audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10);
  };

  const handleVolume = (_: Event, newValue: number | number[]) => {
    const vol = Array.isArray(newValue) ? newValue[0] : newValue;
    if (audioRef.current) audioRef.current.volume = vol / 100;
    setVolume(vol / 100);
  };

  const handlePlaybackSpeed = (speed: number) => {
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
      setPlaybackSpeed(speed);
    }
  };

  useEffect(() => {
    if (!isActive && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    }
  }, [isActive]);

  return (
    <Box
      p={{ xs: 1.5, lg: 2 }}
      mt={{ xs: 1.5, lg: 2 }}
      borderRadius={2}
      display='flex'
      flexDirection='column'
      alignItems='center'
      width='100%'
      border='1px solid #ececec'
      sx={{ bgcolor: '#fff' }}
    >
      <Box display='flex' width='100%' alignItems='center' mb={{ xs: 1, lg: 1.5 }} gap={1}>
        <IoTimeOutline size={isPlaying ? 20 : 18} />
        <Typography mr='auto' variant='body2' fontWeight='500' sx={{ fontSize: { xs: '12px', lg: '14px' } }}>
          Recording
        </Typography>
      </Box>
      <audio ref={audioRef} src={url} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} />
      <Box display='flex' width='100%' p={{ xs: 1.5, lg: 2 }} borderRadius={2} bgcolor='#f8f9fa' flexDirection='column'>
        <Slider
          value={currentTime}
          max={duration}
          onChange={handleSeek}
          sx={{
            mr: { xs: 0, lg: 1 },
            flex: 1,
            '& .MuiSlider-thumb': { width: { xs: 16, lg: 20 }, height: { xs: 16, lg: 20 } }
          }}
        />
        <Box display='flex' justifyContent='space-between' mt={{ xs: 0.5, lg: 0 }}>
          <Typography variant='body2' sx={{ fontSize: { xs: '11px', lg: '14px' } }}>
            {formatTime(currentTime)}
          </Typography>
          <Typography variant='body2' sx={{ fontSize: { xs: '11px', lg: '14px' } }}>
            {formatTime(duration)}
          </Typography>
        </Box>
        <Box
          display='flex'
          justifyContent='space-between'
          bgcolor='#f8f9fa'
          alignItems='center'
          width='100%'
          flexDirection={{ xs: 'column', lg: 'row' }}
          gap={{ xs: 1.5, lg: 0 }}
          mt={{ xs: 1.5, lg: 0 }}
        >
          {/* Left controls */}
          <Box display='flex' alignItems='center' gap={{ xs: 1, lg: 2 }}>
            <IconButton onClick={handleRewind} sx={{ padding: { xs: '4px', lg: '8px' } }}>
              <MdOutlineReplay10 size={isPlaying ? 20 : 18} />
            </IconButton>

            <IconButton onClick={handlePlayPause} sx={{ p: 0 }}>
              <Box
                bgcolor={theme.palette.primary.main}
                color='white'
                display='flex'
                alignItems='center'
                justifyContent='center'
                borderRadius='50%'
                width={{ xs: 36, lg: 40 }}
                height={{ xs: 36, lg: 40 }}
              >
                {isPlaying ? (
                  <TiMediaPauseOutline size={isPlaying ? 14 : 16} />
                ) : (
                  <IoIosPlay size={isPlaying ? 14 : 16} />
                )}
              </Box>
            </IconButton>

            <IconButton onClick={handleForward} sx={{ padding: { xs: '4px', lg: '8px' } }}>
              <MdForward10 size={isPlaying ? 20 : 18} />
            </IconButton>
          </Box>
          <Box
            display='flex'
            alignItems='center'
            gap={{ xs: 1, lg: 3 }}
            width={{ xs: '100%', lg: 'auto' }}
            justifyContent={{ xs: 'space-between', lg: 'flex-end' }}
          >
            <MdOutlineVolumeUp size={isPlaying ? 20 : 18} />
            <Slider
              value={volume * 100}
              onChange={handleVolume}
              sx={{
                width: { xs: '100%', lg: 80 },
                maxWidth: { xs: 'none', lg: 80 },
                flex: { xs: 1, lg: 'none' },
                '& .MuiSlider-thumb': { width: { xs: 16, lg: 20 }, height: { xs: 16, lg: 20 } }
              }}
            />
            <Box>
              <Select
                value={playbackSpeed}
                onChange={(e) => handlePlaybackSpeed(Number(e.target.value))}
                sx={{
                  height: { xs: '28px', lg: '32px' },
                  fontSize: { xs: '12px', lg: '14px' },
                  minWidth: { xs: '60px', lg: '70px' },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e0e0e0'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main
                  }
                }}
              >
                <MenuItem value={0.75}>0.75x</MenuItem>
                <MenuItem value={1}>1x</MenuItem>
                <MenuItem value={1.25}>1.25x</MenuItem>
                <MenuItem value={1.5}>1.5x</MenuItem>
                <MenuItem value={2}>2x</MenuItem>
              </Select>
              <IconButton href={url} download sx={{ padding: { xs: '4px', lg: '8px' }, flexShrink: 0 }}>
                <FiDownload size={isPlaying ? 16 : 18} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MusicPlayer;
